import { jsPDF } from "jspdf";
import _ from "lodash";
import {
  CategoryData,
  CategoryType,
  EatingHabitsData,
  ExerciseHistoryData,
  MedicationFeedbackData,
  MentalHealthHistoryData,
  OtherWeightManagementHistoryData,
  WeightHistoryData,
  WeightLossProgressData,
  WeightTreatmentHistoryData,
} from "src/revamp/types/PatientFormTypes";
import { User } from "src/services/dto/Security";

import { Medication } from "../../types/MedicationTypes";
import { calculateBMI, formatPhoneNumber, getName } from "../helpers";
import { PlexFont, PoppinNormal } from "./constants";
import {
  PDFGeneratorState,
  drawExercise,
  drawFooter,
  drawHeader,
  drawRow,
  drawSectionTitle,
  drawTitle,
  drawValue,
  endSection,
  getDesc,
  getMedicationDosage,
  getMedicationName,
  getValue,
  listItems,
  drawTextBody,
} from "./helper";

const drawPatientInfo = (state: PDFGeneratorState) => {
  drawRow(state, "Name:", getName(state.patient));
  drawRow(state, "Date of Birth:", state.patient.dateOfBirth);
  drawRow(state, "Gender:", state.patient.gender);
  drawRow(state, "Phone:", formatPhoneNumber(state.patient.phone));
  drawRow(state, "Email:", state.patient.email);

  endSection(state);
};

const drawObjective = (state: PDFGeneratorState) => {
  drawSectionTitle(state, "Objective:");
  const reviewOfSystems = getValue(state, "reviewOfSystems");
  const height = reviewOfSystems.height;
  const weight = reviewOfSystems.weight;
  const unit = reviewOfSystems.unit;

  const heightStr =
    unit === "Imperial" ? `${height.feet || 0}'${height.inches || 0}"` : `${height.cm || 0} cm`;

  const weightStr = unit === "Imperial" ? `${weight.pounds || 0} lbs` : `${weight.kg || 0} kg`;

  const bmi = calculateBMI({ height, weight, unit });

  drawValue(state, "Height:", heightStr);
  drawValue(state, "Weight:", weightStr);
  drawValue(state, "BMI:", bmi.toFixed(1));

  drawValue(state, "Recent Symptoms:");
  const recentSymptoms = getValue(state, "reviewOfSystems.recentSymptoms") || [];
  listItems(state, recentSymptoms);

  endSection(state);
};

const drawMedications = (state: PDFGeneratorState) => {
  drawSectionTitle(state, "Medications:");
  const medications = getValue(state, "medications.activeMedications") || [];

  if (_.isEmpty(medications)) {
    drawValue(state, "No medications reported");
  } else {
    drawValue(state, "The patient is currently taking the following medications:");
    medications.forEach((med: Medication) => {
      const name = getMedicationName(med);
      const dosage = getMedicationDosage(med);
      const frequency = med.frequency || "";
      drawValue(state, `- ${name}: ${dosage} ${frequency}`);
    });
  }
  endSection(state);
};

const drawAllergies = (state: PDFGeneratorState) => {
  drawSectionTitle(state, "Allergies:");
  const allergies = getValue(state, "medications.medicationAllergies") || [];

  if (_.isEmpty(allergies)) {
    drawValue(state, "No allergies reported");
  } else {
    drawValue(state, "The patient is allergic to the following medications:");
    allergies.forEach((allergy: any) => {
      drawValue(state, `- ${allergy.name}`);
    });
  }

  endSection(state);
};

const drawMedicationFeedback = (state: PDFGeneratorState) => {
  drawSectionTitle(state, "Medication Feedback");
  const medicationFeedback = getValue(state, "medicationFeedback") as MedicationFeedbackData;

  drawValue(state, "Medication Feedback from Patient:");
  drawTextBody(state, medicationFeedback.medicationFeedback);

  drawValue(state, "Side Effects Reported by Patient:");
  listItems(state, medicationFeedback.medicationSymptoms);

  endSection(state);
};

const drawExerciseHistory = (state: PDFGeneratorState) => {
  drawSectionTitle(state, "Exercise History");
  const exercise = getValue(state, "exerciseHistory") as ExerciseHistoryData;

  if (_.isEmpty(exercise.exercises)) {
    drawValue(state, "No exercise history reported");
  } else {
    exercise.exercises.forEach((exercise) => drawExercise(state, exercise));
  }

  endSection(state);
};

const drawWeightLossProgress = (state: PDFGeneratorState) => {
  drawSectionTitle(state, "Weight Loss Progress");
  const weightLossProgress = getValue(state, "weightLossProgress") as WeightLossProgressData;

  drawValue(state, "Weight Loss Progress Reported by Patient:");
  drawTextBody(state, weightLossProgress.weightLossProgress);

  drawValue(state, "Changes in Sleep:");
  const changesInSleep = _.get(weightLossProgress, "changesInSleep");
  if (changesInSleep?.response) {
    drawTextBody(state, changesInSleep?.description || "");
  } else {
    drawValue(state, "No changes in sleep reported");
  }

  drawValue(state, "Additional Comments from Patient:");
  drawTextBody(state, weightLossProgress.additionalComments);

  endSection(state);
};

// Main generator function
export const generateWMFollowUpPDF = (
  patient: User,
  formData: Record<CategoryType, CategoryData>,
): jsPDF => {
  // Initialize state
  const state: PDFGeneratorState = {
    doc: new jsPDF("p", "mm", "letter"),
    y: 12,
    patient,
    formData,
  };

  // Setup fonts
  state.doc.addFileToVFS("Poppins-SemiBold.ttf", PlexFont);
  state.doc.addFont("Poppins-SemiBold.ttf", "PoppinsBold", "normal");
  state.doc.addFileToVFS("Poppins-Regular.ttf", PoppinNormal);
  state.doc.addFont("Poppins-Regular.ttf", "PoppinsNormal", "normal");

  // Draw content
  drawHeader(state);
  drawTitle(state, "Weight Management Follow-Up Form");

  drawPatientInfo(state);
  drawObjective(state);
  drawMedications(state);
  drawAllergies(state);
  drawMedicationFeedback(state);
  drawExerciseHistory(state);
  drawWeightLossProgress(state);

  drawFooter(state);

  return state.doc;
};